import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";
import Switch from "../../components/Switch";
import useApiUrls from "../../useApiUrls";
import { useRef } from "react";
import Pagination from "../components/Pagination";

// JSON Editor Modal Component
const JsonEditorModal = ({ show, onClose, item, onSave, token, userid }) => {
  const [jsonData, setJsonData] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show && item && item.json) {
      try {
        const parsed = JSON.parse(item.json);
        const pairs = Object.entries(parsed).map(([key, value]) => ({
          key,
          value: JSON.stringify(value),
        }));
        setJsonData(pairs);
        setError("");
      } catch (err) {
        setJsonData([]);
        setError("Invalid JSON format. Starting with empty object.");
      }
    } else if (show) {
      setJsonData([]);
    }
  }, [show, item]);

  const handleKeyChange = (index, newKey) => {
    const newData = [...jsonData];
    newData[index].key = newKey;
    setJsonData(newData);
  };

  const handleValueChange = (index, newValue) => {
    const newData = [...jsonData];
    newData[index].value = newValue;
    setJsonData(newData);
  };

  const addRow = () => {
    setJsonData([...jsonData, { key: "", value: "" }]);
  };

  const removeRow = (index) => {
    const newData = [...jsonData];
    newData.splice(index, 1);
    setJsonData(newData);
  };

const handleSave = async () => {
  try {
    const jsonObject = {};

    const keys = jsonData.map((item) => item.key);
    const hasDuplicates = keys.some(
      (key, index) => key && keys.indexOf(key) !== index
    );

    if (hasDuplicates) {
      setError("Error: Duplicate keys found. Please ensure all keys are unique.");
      return;
    }

    jsonData.forEach((pair) => {
      if (pair.key.trim() !== "") {
        try {
          jsonObject[pair.key] = JSON.parse(pair.value);
        } catch (e) {
          jsonObject[pair.key] = pair.value;
        }
      }
    });

    const jsonString = JSON.stringify(jsonObject, null, 2);

    // ✅ Fixed payload with correct field names and proper fallbacks
    const payload = {
      id: item.id,
      json: jsonString,
      Active: item.Active ?? item.active ?? true,
      LockKey: item.LockKey ?? item.lockkey ?? false,
      DeviceId: item.DeviceId ?? item.deviceid ?? 0, // Fixed: Use DeviceId (capital D) and deviceid as fallback
      SerialId: item.SerialId ?? item.serialid ?? "default-serial-id", // Fixed: Use SerialId (capital S) and serialid as fallback
      model: item.model ?? "default-model"
    };

    const response = await fetch(
      "https://api.dqr-display-tms.eventum.co.in/api/AWSIOTCoreSettingOperation/Update",
      {
        method: "POST",
        headers: {
          "accept": "text/plain",
          "UserId": userid,
          "X-Authorization": token,
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (response.ok) {
      const result = await response.text();
      onSave(jsonString);
      onClose();
    } else {
      const errorText = await response.text();
      console.error("Update failed:", errorText);
      setError("Update failed: " + errorText);
    }
  } catch (err) {
    console.error("Error saving JSON:", err);
    setError(`Error saving JSON: ${err.message}`);
  }
};

  if (!show) return null;

  return (
    <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content d-flex flex-column" style={{ maxHeight: "90vh" }}>
          {/* Modal Header */}
          <div className="modal-header">
            <h5 className="modal-title">
              Edit JSON for {item.settingname || item.MQTTSettingname}
            </h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>

          {/* Modal Body */}
          <div className="modal-body" style={{ flex: '1 1 auto', overflowY: 'auto' }}>
            {error && <div className="alert alert-danger">{error}</div>}
            <div className="table-responsive">
              <table className="table table-bordered">
                 <thead className="thead-light" style={{
                            backgroundColor: 'var(--surface2)',
                            color: 'var(--text)',
                            borderColor: 'var(--border)'
                          }}>
                  <tr>
                    <th style={{ width: "40%" }}>Key</th>
                    <th style={{ width: "50%" }}>Value</th>
                    <th style={{ width: "10%" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jsonData.map((pair, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="text"
                          className="form-control"
                          value={pair.key}
                          onChange={(e) => handleKeyChange(index, e.target.value)}
                          placeholder="Property name"
                        />
                      </td>
                      <td>
                        <textarea
                          className="form-control"
                          value={pair.value}
                          onChange={(e) => handleValueChange(index, e.target.value)}
                          placeholder="Value (string, number, object, array)"
                          rows="2"
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => removeRow(index)}
                          title="Remove row"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button className="btn btn-info btn-sm" onClick={addRow}>
              <i className="fas fa-plus"></i> Add Property
            </button>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() =>handleSave()}>
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const JsonViewMode = ({ show, onClose, item }) => {
  // If the modal shouldn't be shown, return null
  if (!show) return null;

  // Function to format JSON nicely
  const formatJson = (jsonString) => {
    try {
      // Parse the JSON string and then stringify it with indentation
      const parsedJson = JSON.parse(jsonString);
      return JSON.stringify(parsedJson, null, 2);
    } catch (error) {
      return "Invalid JSON format";
    }
  };

  return (
    <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">View JSON for {item.settingname || item.MQTTSettingname}</h5>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body" style={{ maxHeight: "400px", overflowY: "auto", marginBottom: "10px" }}>
            <pre style={{ 
              backgroundColor: "#f8f9fa", 
              padding: "15px", 
              borderRadius: "4px",
              maxHeight: "500px",
              overflow: "auto"
            }}>
              {item && item.json ? formatJson(item.json) : "No JSON data available"}
            </pre>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Root File Upload Modal Component
const RootFileUploadModal = ({ show, onClose, token, userid, onSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const isValidFile = 
        file.type === 'application/zip' ||
        file.type === 'application/x-zip-compressed' ||
        file.name.endsWith('.zip') ||
        file.name.endsWith('.pem');

      if (isValidFile) {
        setSelectedFile(file);
        setUploadStatus(null);
      } else {
        setSelectedFile(null);
        setUploadStatus({
          type: 'error',
          message: 'Please select a valid ZIP or PEM file'
        });
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      const isValidFile = 
        file.type === 'application/zip' ||
        file.type === 'application/x-zip-compressed' ||
        file.name.endsWith('.zip') ||
        file.name.endsWith('.pem');

      if (isValidFile) {
        setSelectedFile(file);
        setUploadStatus(null);
      } else {
        setUploadStatus({
          type: 'error',
          message: 'Please drop a valid ZIP or PEM file'
        });
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus({
        type: 'error',
        message: 'Please select a file first'
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(
        'https://api.dqr-display-tms.eventum.co.in/api/AWSIOTCoreSettingOperation/Upload/Zip',
        formData,
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid,
            'X-Authorization': token,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );

      if (response.data) {
        setUploadStatus({
          type: 'success',
          message: 'Root file uploaded successfully!'
        });

        setTimeout(() => {
          onSuccess && onSuccess();
          onClose();
          // Reset form
          setSelectedFile(null);
          setUploadStatus(null);
          setUploadProgress(0);
        }, 2000);
      } else {
        setUploadStatus({
          type: 'error',
          message: 'Upload failed: Unknown error'
        });
      }
    } catch (error) {
      console.error('Error uploading root file:', error);
      setUploadStatus({
        type: 'error',
        message: `Upload failed: ${error.response?.data?.errormsg || error.message || 'Server error'}`
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setSelectedFile(null);
      setUploadStatus(null);
      setUploadProgress(0);
      onClose();
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: '#007bff', color: 'white' }}>
            <h5 className="modal-title">
              <i className="fas fa-upload mr-2"></i>
              Upload Root File
            </h5>
            <button
              type="button"
              className="close text-white"
              onClick={handleClose}
              disabled={uploading}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            {/* File input (hidden) */}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              accept=".zip,.pem"
            />

            {/* Drag and drop area */}
            <div
              className="text-center p-4 mb-3"
              style={{
                border: '2px dashed #ccc',
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={triggerFileInput}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#007bff';
                e.target.style.backgroundColor = '#e7f3ff';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#ccc';
                e.target.style.backgroundColor = '#f8f9fa';
              }}
            >
              <i className="fas fa-cloud-upload-alt fa-3x mb-3 text-primary"></i>
              <h6>Drop your root file here</h6>
              <p className="text-muted mb-0">or click to browse</p>
              <small className="text-muted">Supported formats: .zip, .pem</small>
            </div>

            {/* Selected file info */}
            {selectedFile && (
              <div className="mb-3 p-3 rounded" style={{ backgroundColor: '#e8f5e8', border: '1px solid #c3e6c3' }}>
                <div className="d-flex align-items-center">
                  <i className="fas fa-file-archive text-success mr-2"></i>
                  <div className="flex-grow-1">
                    <strong>Selected file:</strong> {selectedFile.name}
                    <br />
                    <small className="text-muted">
                      Size: {(selectedFile.size / 1024).toFixed(2)} KB
                    </small>
                  </div>
                </div>
              </div>
            )}

            {/* Upload progress */}
            {uploading && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="progress">
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: `${uploadProgress}%` }}
                    aria-valuenow={uploadProgress}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  />
                </div>
              </div>
            )}

            {/* Status messages */}
            {uploadStatus && (
              <div className={`alert alert-${uploadStatus.type === 'success' ? 'success' : 'danger'} mb-3`}>
                <i className={`fas ${uploadStatus.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'} mr-2`}></i>
                {uploadStatus.message}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={uploading}
            >
              <i className="fas fa-times mr-1"></i>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
            >
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="fas fa-upload mr-1"></i>
                  Upload
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get cookie value
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

const AWSIOTCoreList = () => {
  const [data, setData] = useState([]);
  const [AWSIOTCoreListId, setAWSIOTCoreListId] = useState([]);
  const [message, setMessage] = useState("");
  const [publishInfo, setPublishInfo] = useState({}); // State to hold publish info
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [publishLoading, setPublishLoading] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [jsonViewModalOpen, setJsonViewModalOpen] = useState(false);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showRootFileModal, setShowRootFileModal] = useState(false);
  
  // Pagination states
  const [dataLimit, setDataLimit] = useState(100);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  
  const fileInputRef = useRef(null);

  // Handle file upload
  const handleUploadFile = async () => {
    if (!selectedFile) {
      setUploadStatus({
        type: 'error',
        message: 'Please select a file first'
      });
      return;
    }

    const token = getCookie("token");
    const userid = getCookie("userid");

    if (!token || !userid) {
      setUploadStatus({
        type: 'error',
        message: 'Authentication error. Please log in again.'
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // Create FormData
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(
        'https://api.dqr-display-tms.eventum.co.in/api/AWSIOTCoreSettingOperation/Upload',
        formData,
        {
          headers: {
            'X-Authorization': token,
            'UserId': userid,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );

      // Handle the response
      if (response.data && response.data.isresponse && response.data.responsestatus === "SUCCESS") {
        setUploadStatus({
          type: 'success',
          message: 'File uploaded successfully!'
        });

        // Close the modal after a successful upload (with a slight delay)
        setTimeout(() => {
          setShowModal(false);
          // Optionally refresh the page or trigger another function
          // window.location.reload();
        }, 2000);
      } else {
        setUploadStatus({
          type: 'error',
          message: `Upload failed: ${response.data?.errormsg || 'Unknown error'}`
        });
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadStatus({
        type: 'error',
        message: `Upload failed: ${error.response?.data?.errormsg || error.message || 'Server error'}`
      });
    } finally {
      setUploading(false);
    }
  };

  const { getUrlByOperation } = useApiUrls();
  const navigate = useNavigate();
  
  // Get authentication credentials from cookies
  const token = getCookie("token");
  const userid = getCookie("userid");

  // Fetch catalog and AWSIOTCoreList data
  useEffect(() => {
    const fetchCatalogAndAWSIOTCoreListData = async () => {
      setIsLoading(true);
      try {
        const requestBody = {
          id: 0,
          applycreateddate: false,
          applymodifieddate: false,
          fromcreateddate: 0,
          endcreateddate: 0,
          frommodifieddate: 0,
          endmodifieddate: 0,
          active: "",
          catalogname: "",
          ispageload: false,
          pagesize: dataLimit,
          currentpageno: count,
          withoutpagination: false,
          pagedirection: "first",
        };
        
        const CatalogSearch = getUrlByOperation("CatalogSearch");
        const AWSIOTCoreListGetDistinct = getUrlByOperation("AWSIOTCoreListGetDistinct");

        // Fetch catalog data
        const catalogResponse = await axios.post('https://api.dqr-display-tms.eventum.co.in/api/AWSIOTCoreSettingOperation/Search', 
          requestBody, 
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "application/json-patch+json",
            },
          }
        );
        
        if (catalogResponse.data && Array.isArray(catalogResponse.data.data)) {
          const rows = catalogResponse.data.data;
          setData(rows);
          setHasMore(rows.length === Number(dataLimit));
          const approximateTotal = (count * Number(dataLimit)) + rows.length + (rows.length === Number(dataLimit) ? Number(dataLimit) : 0);
          setTotalItems(approximateTotal);
        }

        // Note: AWSIOTCoreListResponse is not defined in the original code
        // if (AWSIOTCoreListResponse.data && Array.isArray(AWSIOTCoreListResponse.data.data)) {
        //   setAWSIOTCoreListId(AWSIOTCoreListResponse.data.data);
        // }
      } catch (error) {
        console.error("Error fetching data:", error);
        
        // Handle token expiration
        if (error.response?.status === 401 || error.message === "relogin token expire") {
          navigate("/login");
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (token && userid) {
      fetchCatalogAndAWSIOTCoreListData();
    } else {
      navigate("/login");
    }
  }, [getUrlByOperation, token, userid, dataLimit, count, navigate]);

  const handlePageChange = (page) => {
    setCount(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0);
  };

  const totalPages = hasMore ? count + 2 : count + 1;

  // Navigation handlers
  const handleAddPop = () => {
    navigate("/AddAWSIOTCore");
  };

  const handleUpdatePop = (id) => {
    navigate(`/updatecatelog/${id}`);
  };

  const handleShowImagePop = (id) => {
    navigate(`/showcatelog/${id}`);
  };

  const handlePreview = (id) => {
    const previewUrl = `/catelogpreview/${id}`;
    window.open(previewUrl, "_blank");
  };

  const unlockAll = async () => {
    try {
      const response = await axios.get(
        'https://api.dqr-display-tms.eventum.co.in/api/AWSIOTCoreSettingOperation/UpdateStatus/lockkey/DeactiveAll',
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid, // Use actual user ID or from cookies
            'X-Authorization': token, // Replace with actual token
          },
        }
      );
  
      if (response.status === 200) {
        // Show success message or refresh list
      } else {
        console.warn("Unlock All failed with status:", response.status);
      }
    } catch (error) {
      console.error("Unlock All failed:", error);
    }
  };

  const handleUploadExcel = () => {
    setShowModal(true);
    // Reset the state when opening the modal
    setSelectedFile(null);
    setUploadStatus(null);
    setUploadProgress(0);
  };

  const handleRootFileAdd = () => {
    setShowRootFileModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate if it's an Excel or CSV file
      const fileName = file.name.toLowerCase();
      const isExcel =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'text/csv' ||
        fileName.endsWith('.xlsx') ||
        fileName.endsWith('.xls') ||
        fileName.endsWith('.csv');

      if (isExcel) {
        setSelectedFile(file);
        setUploadStatus(null);
      } else {
        setSelectedFile(null);
        setUploadStatus({
          type: 'error',
          message: 'Please select a valid Excel or CSV file (.xlsx, .xls, .csv)'
        });
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      const fileName = file.name.toLowerCase();

      // Check if it's an Excel or CSV file
      const isExcel =
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'text/csv' ||
        fileName.endsWith('.xlsx') ||
        fileName.endsWith('.xls') ||
        fileName.endsWith('.csv');

      if (isExcel) {
        setSelectedFile(file);
        setUploadStatus(null);
      } else {
        setUploadStatus({
          type: 'error',
          message: 'Please drop a valid Excel or CSV file (.xlsx, .xls, .csv)'
        });
      }
    }
  };

  // Handle drag and drop functionality
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Encode ID for publishing
  const encodeId = (id) => {
    const strId = id.toString();
    const base64Id = btoa(strId);
    return base64Id.substring(0, 10).padEnd(8, "0"); // Limit to 8-10 characters
  };

  // const handleSaveJson = async (jsonString) => {
  //   try {
  //     // Parse to validate
  //     JSON.parse(jsonString);

  //     // Prepare the update payload
  //     const updatePayload = {
  //       id: currentItem.id,
  //       active: currentItem.active,
  //       lockkey:currentItem.lockkey,
  //       remark: currentItem.remark,
  //       settingname: currentItem.settingname || currentItem.MQTTSettingname,
  //       settingtype: currentItem.settingtype || "default",
  //       json: jsonString,
  //       SerialId: currentItem.SerialId || currentItem.serialid, // Use SerialId or serialid as fallback
  //       DeviceId:currentItem.DeviceId || currentItem.deviceid || 0, deviceid// Use DeviceId or deviceid as fallback
  //     };

  //     // Send update request
  //     const response = await fetch('https://api.dqr-display-tms.eventum.co.in/api/AWSIOTCoreSettingOperation/Update', {
  //       method: 'POST',
  //       headers: {
  //         'accept': 'text/plain',
  //         'UserId': userid || '1',
  //         'X-Authorization': token,
  //         'Content-Type': 'application/json'
  //       },
  //       body: JSON.stringify(updatePayload)
  //     });

  //     if (response.ok) {
  //       const result = await response.json();

  //       setMessage("JSON updated successfully!");

  //       // Update the data in the state
  //      setData(data.map(item =>
  //     item.id === currentItem.id
  //       ? { ...item, json: jsonString }
  //       : item
  //   ));
  //   setMessage("JSON updated successfully!");
  //     } else {
  //       throw new Error("Update failed: " + (await response.text()));
  //     }
  //   } catch (error) {
  //     console.error("Error updating JSON:", error);
  //     setMessage("Failed to update JSON: " + error.message);
  //   }
  // };

  const handleSaveJson = async (jsonString) => {
  try {
    // Parse and validate JSON
    const parsedJson = JSON.parse(jsonString);

    // Extract DeviceId from jsonString
    const extractedDeviceId = parsedJson.deviceid || parsedJson.DeviceId || 0;

    // Prepare the update payload
    const updatePayload = {
      id: currentItem.id,
      active: currentItem.active,
      lockkey: currentItem.lockkey,
      remark: currentItem.remark,
      settingname: currentItem.settingname || currentItem.MQTTSettingname,
      settingtype: currentItem.settingtype || "default",
      json: jsonString,
      SerialId: currentItem.SerialId || currentItem.serialid,
      DeviceId: extractedDeviceId
    };

    // Send update request
    const response = await fetch('https://api.dqr-display-tms.eventum.co.in/api/AWSIOTCoreSettingOperation/Update', {
      method: 'POST',
      headers: {
        'accept': 'text/plain',
        'UserId': userid || '1',
        'X-Authorization': token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updatePayload)
    });

    if (response.ok) {
      const result = await response.json();
      setMessage("JSON updated successfully!");

      // Update the state data
      setData(data.map(item =>
        item.id === currentItem.id
          ? { ...item, json: jsonString }
          : item
      ));
    } else {
      throw new Error("Update failed: " + (await response.text()));
    }
  } catch (error) {
    console.error("Error updating JSON:", error);
    setMessage("Failed to update JSON: " + error.message);
  }
};


  const handleCopyClick = async (id, publishId) => {
    const publishUrl = `${window.location.origin}/TOPWise_aws/${publishId}`;
    
    try {
      await copyToClipboard(publishUrl);
      setMessage("URL copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy URL:", error);
    }
  };

const handlePublish = async (id) => {
  try {
    setIsLoading(true);

    const response = await axios.post(
      "https://api.dqr-display-tms.eventum.co.in/api/AWSIOTCoreSettingOperation/Generate/PublishId",
      { id },
      {
        headers: {
          accept: "text/plain",
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
        },
      }
    );

    if (response.data.isresponse) {
      let publishId = response.data.data || response.data;

      // Ensure publishId is a string before using it in rendering
      if (typeof publishId === "object") {
        publishId = JSON.stringify(publishId);
      }

      const publishUrl = `${window.location.origin}/catelogpreview-publish/${publishId}`;
      setPublishInfo({ id, encodedUrl: publishUrl, encodedId: publishId });

      try {
        await navigator.clipboard.writeText(publishUrl);
        setMessage("Publish ID generated and URL copied to clipboard!");
      } catch (clipboardError) {
        setMessage("Publish ID generated, but failed to copy URL");
      }
    } else {
      // const fallbackMsg = typeof response.data.errormsg === "string"
      //   ? response.data.errormsg
      //   : JSON.stringify(response.data);
      // setMessage(`Failed to generate publish ID: ${fallbackMsg}`);
    }

  } catch (error) {
    console.error("Error generating publish ID:", error);

    const errorMessage = error.response?.data
      ? JSON.stringify(error.response.data)
      : error.message;

    setMessage(`Failed to generate publish ID: ${errorMessage}`);

    if (
      error.response?.status === 401 ||
      error.message === "relogin token expire"
    ) {
      navigate("/login");
    }
  } finally {
    setIsLoading(false);
  }
};

function fallbackCopyToClipboard(text) {
  if (text === null) {
    console.error('Cannot copy null to clipboard');
    return Promise.reject(new Error('Cannot copy null to clipboard'));
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.left = "-999999px";
  textArea.style.top = "-999999px";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  return new Promise((resolve, reject) => {
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (successful) {
        resolve(true);
      } else {
        reject(new Error('Copy command was unsuccessful'));
      }
    } catch (err) {
      document.body.removeChild(textArea);
      reject(err);
    }
  });
}

function copyToClipboard(text) {
  // Try to use the Clipboard API first
  if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text)
      .then(() => true)
      .catch(err => {
        console.error('Could not copy text using Clipboard API: ', err);
        return fallbackCopyToClipboard(text);
      });
  } else {
    // Fall back to the textarea method
    return Promise.resolve(fallbackCopyToClipboard(text));
  }
}

  // Handle delete functionality
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) {
      return;
    }

    const requestBody = { id };

    try {
      const CatalogDelete = getUrlByOperation("CatalogDelete");
      await axios.post('https://api.dqr-display-tms.eventum.co.in/api/AWSIOTCoreSettingOperation/Delete', 
        requestBody, 
        {
          headers: {
            "X-Authorization": token,
            UserId: userid,
            "Content-Type": "application/json-patch+json",
          },
        }
      );
      
      setMessage("Data deleted successfully!");
      setData(data.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Error deleting item:", error);
      setMessage("Failed to delete data");
      
      // Handle token expiration
      if (error.response?.status === 401 || error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  // Handle toggle active/inactive status
  const handleToggle = async (item) => {
    const CatalogActive = getUrlByOperation("CatalogActive") || 
      'https://api.dqr-display-tms.eventum.co.in/api/AWSIOTCoreSettingOperation/UpdateStatus/active/Active';
    const CatalogDeactive = getUrlByOperation("CatalogDeactive") || 
      'https://api.dqr-display-tms.eventum.co.in/api/AWSIOTCoreSettingOperation/UpdateStatus/active/Deactive';
    
    const apiUrl = item.active ? CatalogDeactive : CatalogActive;
    const requestBody = { id: item.id };

    try {
      await axios.post(apiUrl, requestBody, {
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
        },
      });
      
      // Update local state
      setData(
        data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d))
      );
      
      setMessage(`Item ${!item.active ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error("Error updating status:", error);
      
      // Handle token expiration
      if (error.response?.status === 401 || error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  const renderJsonColumn = (item) => {
    return (
      <div className="json-preview">
        <div className="mb-2">
          {item.json ? (
            <div
              className="text-truncate"
              style={{
                maxWidth: '150px',
                maxHeight: '50px',
                overflow: 'hidden',
                padding: '4px',
                // backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #dee2e6',
                fontSize: '0.8rem'
              }}
            >
              {item.json}
            </div>
          ) : (
            <span className="text-muted">Empty JSON</span>
          )}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-primary ml-3"
          onClick={() => handleEditJson(item)}
        >
          <i className="fas fa-edit "></i>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-primary ml-1"
          onClick={() => handleViewPOP(item)}
        >
          <i className="fas fa-eye "></i>
        </button>
      </div>
    );
  };

  // Auto-hide message after 3 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleUnPublish = async (id, publishId) => {
    try {
      const response = await axios.post(
        'https://api.dqr-display-tms.eventum.co.in/api/AWSIOTCoreSettingOperation/UnPublishId',
        { id },
        {
          headers: {
            'accept': 'text/plain',
            'UserId': userid || '1',
            'X-Authorization': token,
            'Content-Type': 'application/json-patch+json'
          }
        }
      );
      
      if (response.data && response.data.isresponse) {
        setMessage(" unpublished successfully!");
        // fetchCampaignData(); // Uncomment if this function exists
      } else {
        throw new Error(response.data?.errormsg || "Failed to unpublish ");
      }
    } catch (error) {
      console.error("Error unpublishing :", error);
      setMessage(error.message || "Failed to unpublish ");
    }
  };

  const handleCopyHttp = (key) => {
    setMessage("URL copied to clipboard!");
    const urlToCopy = `http://api.dqr-display-tms.eventum.co.in/api/Share/AWSIOTCore/publishId/File/${key}`;
    navigator.clipboard.writeText(urlToCopy)
      .then(() => {
        setMessage("URL copied to clipboard!");
        setTimeout(() => setMessage(""), 2000); // hide message after 2s
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const handleCopyHttps = (key) => {
    setMessage("URL copied to clipboard!");
    const urlToCopy = `https://api.dqr-display-tms.eventum.co.in/api/Share/AWSIOTCore/publishId/File/${key}`;
    navigator.clipboard.writeText(urlToCopy)
      .then(() => {
        setMessage("URL copied to clipboard!");
        setTimeout(() => setMessage(""), 2000); // hide message after 2s
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  // Modified handleCopyJsonHttp function with null check
  const handleCopyJsonHttp = (key) => {
    setMessage("URL copied to clipboard!");
    const urlToCopy = `http://api.dqr-display-tms.eventum.co.in/api/Share/AWSIOTCore/publishId/Json/${key}`;
    navigator.clipboard.writeText(urlToCopy)
        .then(() => {
          setMessage("URL copied to clipboard!");
          setTimeout(() => setMessage(""), 2000); // hide message after 2s
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
        });
  };

  // Modified handleCopyJsonHttps function with null check
  const handleCopyJsonHttps = (key) => {
    setMessage("URL copied to clipboard!");
    const urlToCopy = `https://api.dqr-display-tms.eventum.co.in/api/Share/AWSIOTCore/publishId/Json/${key}`;
    navigator.clipboard.writeText(urlToCopy)
        .then(() => {
          setMessage("URL copied to clipboard!");
          setTimeout(() => setMessage(""), 2000); // hide message after 2s
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
        });
  };

  const handleCopyHttpOne = (key) => {
    setMessage("URL copied to clipboard!");
    const urlToCopy = `http://api.dqr-display-tms.eventum.co.in/api/Share/AWSIOTCore/serialId/File/${key}`;
    navigator.clipboard.writeText(urlToCopy)
      .then(() => {
        setMessage("URL copied to clipboard!");
        setTimeout(() => setMessage(""), 2000); // hide message after 2s
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const handleCopyHttpsOne = (key) => {
    setMessage("URL copied to clipboard!");
    const urlToCopy = `https://api.dqr-display-tms.eventum.co.in/api/Share/AWSIOTCore/serialId/File/${key}`;
    navigator.clipboard.writeText(urlToCopy)
      .then(() => {
        setMessage("URL copied to clipboard!");
        setTimeout(() => setMessage(""), 2000); // hide message after 2s
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  // Modified handleCopyJsonHttp function with null check
  const handleCopyJsonHttpOne = (key) => {
    setMessage("URL copied to clipboard!");
    const urlToCopy = `http://api.dqr-display-tms.eventum.co.in/api/Share/AWSIOTCore/serialId/Json/${key}`;
    navigator.clipboard.writeText(urlToCopy)
        .then(() => {
          setMessage("URL copied to clipboard!");
          setTimeout(() => setMessage(""), 2000); // hide message after 2s
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
        });
  };

  // Modified handleCopyJsonHttps function with null check
  const handleCopyJsonHttpsOne = (key) => {
    setMessage("URL copied to clipboard!");
    const urlToCopy = `https://api.dqr-display-tms.eventum.co.in/api/Share/AWSIOTCore/serialId/Json/${key}`;
    navigator.clipboard.writeText(urlToCopy)
        .then(() => {
          setMessage("URL copied to clipboard!");
          setTimeout(() => setMessage(""), 2000); // hide message after 2s
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
        });
  };

  const handleEditJson = (item) => {
    setCurrentItem(item);
    setJsonModalOpen(true);
  };

  const handleViewPOP = (item) => {
    setCurrentItem(item);
    setJsonViewModalOpen(true); // Use the setter function, not the state variable
  };

  const handleTogglelockkey = async (item) => {
    const apiUrl = item.lockkey ? getUrlByOperation("AWSSettingDeactiveLockkey") : getUrlByOperation("AWSSettingActiveLockkey");

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          "X-Authorization": token,
          UserId: userid,
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify({ id: item.id }),
      });

      if (response.ok) {
        setData(data.map((d) => (d.id === item.id ? { ...d, lockkey: !item.lockkey } : d)));
        setMessage(`Your Status Updated successfully!`);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      if (error.message === "relogin token expire") {
        navigate("/login");
      }
    }
  };

  // Function to render the publish column
  const renderPublishColumn = (item) => {
    // If the item already has a publishid, show it with a copy button
    if (item.publishid) {
      // Truncate publishid if it's longer than 5 characters
      const displayId = item.publishid.length > 5 
        ? `${item.publishid.substring(0, 5)}...` 
        : item.publishid;
        
      return (
        <div className="d-flex flex-column">
          <div className="mb-2">
            <span className="badge badge-info">{displayId}</span>
          </div>
          <div className="btn-group-vertical" style={{ gap: '2px' }}>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleCopyClick(item.id, item.publishid)}
              title="Copy URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
              <i className="fas fa-copy mr-1"></i> URL
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleCopyHttp(item.publishid)}
              title="Copy HTTP URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
              File HTTP
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleCopyHttps(item.publishid)}
              title="Copy HTTPS URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
             File HTTPS
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleCopyJsonHttp(item.publishid)}
              title="Copy HTTP URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
              Json HTTP
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleCopyJsonHttps(item.publishid)}
              title="Copy HTTPS URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
              Json HTTPS
            </button>
            <button
              className="btn btn-sm btn-outline-info"
              onClick={() => handleCopyHttpOne(item.serialid)}
              title="Copy HTTP URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
             One Time File HTTP 
            </button>
            <button
              className="btn btn-sm btn-outline-info"
              onClick={() => handleCopyHttpsOne(item.serialid)}
              title="Copy HTTPS URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
             One Time File HTTPS 
            </button>
            <button
              className="btn btn-sm btn-outline-info"
              onClick={() => handleCopyJsonHttpOne(item.serialid)}
              title="Copy HTTP URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
              One Time Json HTTP 
            </button>
            <button
              className="btn btn-sm btn-outline-info"
              onClick={() => handleCopyJsonHttpsOne(item.serialid)}
              title="Copy HTTPS URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
              One Time Json HTTPS 
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleUnPublish(item.id, item.publishid)}
              title="Unpublish"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
              <i className="fas fa-eye-slash"></i> Unpublish
            </button>
          </div>
        </div>
      );
    }
    
    // Check if this item is currently being processed
    if (publishInfo.id === item.id) {
      // Truncate encodedId if it's longer than 5 characters
      const displayId = publishInfo.encodedId.length > 5 
        ? `${publishInfo.encodedId.substring(0, 5)}...` 
        : publishInfo.encodedId;
        
      return (
        <div className="d-flex flex-column">
          <div className="mb-2">
            <span className="badge badge-info">{displayId}</span>
          </div>
          <div className="btn-group-vertical" style={{ gap: '2px' }}>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleCopyClick(item.id, item.publishid)}
              title="Copy URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
              <i className="fas fa-copy mr-1"></i> URL
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleCopyHttp(item.publishid)}
              title="Copy HTTP URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
              File HTTP
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleCopyHttps(item.publishid)}
              title="Copy HTTPS URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
             File HTTPS
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleCopyJsonHttp(item.publishid)}
              title="Copy HTTP URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
              Json HTTP
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handleCopyJsonHttps(item.publishid)}
              title="Copy HTTPS URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
              Json HTTPS
            </button>
            <button
              className="btn btn-sm btn-outline-info"
              onClick={() => handleCopyHttpOne(item.serialid)}
              title="Copy HTTP URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
             One Time File HTTP 
            </button>
            <button
              className="btn btn-sm btn-outline-info"
              onClick={() => handleCopyHttpsOne(item.serialid)}
              title="Copy HTTPS URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
             One Time File HTTPS 
            </button>
            <button
              className="btn btn-sm btn-outline-info"
              onClick={() => handleCopyJsonHttpOne(item.serialid)}
              title="Copy HTTP URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
              One Time Json HTTP 
            </button>
            <button
              className="btn btn-sm btn-outline-info"
              onClick={() => handleCopyJsonHttpsOne(item.serialid)}
              title="Copy HTTPS URL"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
              One Time Json HTTPS 
            </button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleUnPublish(item.id, item.publishid)}
              title="Unpublish"
              style={{ fontSize: '0.75rem', padding: '2px 6px' }}
            >
              <i className="fas fa-eye-slash"></i> Unpublish
            </button>
          </div>
        </div>
      );
    }
    // If no publishid and not currently processing, show the publish button
    return (
      <button
        className="btn btn-sm btn-success"
        onClick={() => handlePublish(item.id)}
        disabled={publishLoading}
      >
        {publishLoading ? (
          <>
            <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
            Publishing...
          </>
        ) : (
          <>
            <i className="fas fa-globe mr-1"></i> Publish
          </>
        )}
      </button>
    );
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <h1 className="mb-0" style={{ color: '#333', fontWeight: '600' }}>
                    <i className="fas fa-microchip mr-2 text-primary"></i>
                    AWS IoT Core Management
                  </h1>
                  <p className="text-muted mb-0">Manage your IoT device configurations and settings</p>
                </div>
                <div className="col-md-6">
                  <div className="d-flex justify-content-end align-items-center" style={{ gap: '10px' }}>
                  <button 
                      type="button" 
                      className="btn btn-info btn-sm"
                      onClick={unlockAll}
                      style={{
                        borderRadius: '6px',
                        fontWeight: '500',
                        padding: '8px 16px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      <i className="fas fa-upload mr-2"></i>
                      Unlock All Key
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-info btn-sm"
                      onClick={handleRootFileAdd}
                      style={{
                        borderRadius: '6px',
                        fontWeight: '500',
                        padding: '8px 16px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      <i className="fas fa-upload mr-2"></i>
                      Root File Add
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-warning btn-sm"
                      onClick={handleRootFileAdd}
                      style={{
                        borderRadius: '6px',
                        fontWeight: '500',
                        padding: '8px 16px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      <i className="fas fa-file-archive mr-2"></i>
                      Upload Zip File
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-success btn-sm" 
                      onClick={handleUploadExcel}
                      style={{
                        borderRadius: '6px',
                        fontWeight: '500',
                        padding: '8px 16px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      <i className="fas fa-file-excel mr-2"></i>
                      Upload Excel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
          
          <section className="content">
            <div className="container-fluid">
              <div style={{ 
                height: '2px', 
                background: 'linear-gradient(90deg, #007bff, #28a745, #17a2b8)', 
                margin: '20px 0',
                borderRadius: '1px'
              }}></div>
              
              {isLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                  <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="sr-only">Loading...</span>
                    </div>
                    <h5 className="text-muted">Loading AWS IoT Core data...</h5>
                  </div>
                </div>
              ) : (
                <div className="row">
                  <div className="col-12">
                    <div className="card" style={{ 
                      borderRadius: '10px', 
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                      border: 'none'
                    }}>
                      <div className="card-header" style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderBottom: '1px solid #e9ecef',
                        borderRadius: '10px 10px 0 0',
                        padding: '20px'
                      }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="mb-0" style={{ color: '#495057', fontWeight: '600' }}>
                            <i className="fas fa-list mr-2"></i>
                            Device List ({data.length} devices)
                          </h5>
                          <div className="badge badge-primary" style={{ fontSize: '0.9rem', padding: '8px 12px' }}>
                            Total: {data.length}
                          </div>
                        </div>
                      </div>
                      <div className="card-body table-responsive p-0">
                        <table className="table table-hover mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                          <thead style={{ backgroundColor: '#f1f3f4' }}>
                            <tr>
                              <th style={{ width: '8%', padding: '15px', fontWeight: '600', color: '#495057' }} className="text-center">
                                <i className="fas fa-hashtag mr-1"></i>Device ID
                              </th>
                              <th style={{ width: '15%', padding: '15px', fontWeight: '600', color: '#495057' }}>
                                <i className="fas fa-barcode mr-1"></i>Serial ID
                              </th>
                              <th style={{ width: '20%', padding: '15px', fontWeight: '600', color: '#495057' }}>
                                <i className="fas fa-code mr-1"></i>JSON Configuration
                              </th>
                              <th style={{ width: '15%', padding: '15px', fontWeight: '600', color: '#495057' }} className="text-center">
                                <i className="fas fa-cogs mr-1"></i>Actions
                              </th>
                              <th style={{ width: '10%', padding: '15px', fontWeight: '600', color: '#495057' }} className="text-center">
                                <i className="fas fa-lock mr-1"></i>Lock Status
                              </th>
                              <th style={{ width: '10%', padding: '15px', fontWeight: '600', color: '#495057' }} className="text-center">
                                <i className="fas fa-toggle-on mr-1"></i>Status
                              </th>
                              {/* <th style={{ width: '22%', padding: '15px', fontWeight: '600', color: '#495057' }} className="text-center">
                                <i className="fas fa-share-alt mr-1"></i>Publish & Share
                              </th> */}
                            </tr>
                          </thead>
                          <tbody>
                            {data.length === 0 ? (
                              <tr>
                                <td colSpan="7" className="text-center py-5">
                                  <div style={{ padding: '40px 20px' }}>
                                    <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                                    <h5 className="text-muted">No devices found</h5>
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              data.map((item, index) => (
                                <tr key={item.id} style={{ 
                                  transition: 'all 0.2s ease',
                                  borderBottom: '1px solid #f1f3f4'
                                }}>
                                  <td className="text-center" style={{ padding: '15px' }}>
                                    <div style={{
                                      backgroundColor: '#e3f2fd',
                                      color: '#1976d2',
                                      padding: '6px 12px',
                                      borderRadius: '20px',
                                      fontWeight: '600',
                                      display: 'inline-block',
                                      fontSize: '0.85rem'
                                    }}>
                                      {item.deviceid}
                                    </div>
                                  </td>
                                  <td style={{ padding: '15px' }}>
                                    <div style={{ fontWeight: '600', color: '#495057' }}>
                                      {item.serialid || 'N/A'}
                                    </div>
                                  </td>
                                  <td style={{ padding: '15px' }}>
                                    {renderJsonColumn(item)}
                                  </td>
                                  <td className="text-center" style={{ padding: '15px' }}>
                                    <div className="btn-group" role="group">
                                      <button 
                                        className="btn btn-sm btn-outline-warning" 
                                        onClick={() => handleUpdatePop(item.id)}
                                        title="Edit Device"
                                        style={{ borderRadius: '4px 0 0 4px' }}
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                     
                                      <button 
                                        className="btn btn-sm btn-outline-danger" 
                                        onClick={() => handleDelete(item.id)}
                                        title="Delete Device"
                                        style={{ borderRadius: '0 4px 4px 0' }}
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                  <td className="text-center" style={{ padding: '15px' }}>
                                    <Switch
                                      isOn={item.lockkey}
                                      handleToggle={() => handleTogglelockkey(item)}
                                      id={`lockkey-switch-${item.id}`}
                                    />
                                  </td>
                                  <td className="text-center" style={{ padding: '15px' }}>
                                    <Switch 
                                      isOn={item.active} 
                                      handleToggle={() => handleToggle(item)} 
                                      id={`active-switch-${item.id}`}
                                    />
                                  </td>
                                  {/* <td className="text-center" style={{ padding: '15px' }}>
                                    {renderPublishColumn(item)}
                                  </td> */}
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                        <Pagination
                          currentPage={count + 1}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                          itemsPerPage={dataLimit}
                          onItemsPerPageChange={handleItemsPerPageChange}
                          totalItems={totalItems}
                          showPageNumbers={false}
                          showItemsPerPageSelector={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Message Alert */}
      {message && (
        <div style={{ 
          position: "fixed", 
          top: "20px", 
          right: "20px", 
          width: "350px", 
          zIndex: "9999" 
        }}>
          <div
            className={`alert ${
              message.includes("successfully") || message.includes("copied") 
                ? "alert-success" 
                : "alert-danger"
            } d-flex align-items-center alert-dismissible fade show`}
            role="alert"
            style={{
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              border: 'none'
            }}
          >
            <i className={`fas ${
              message.includes("successfully") || message.includes("copied")
                ? "fa-check-circle" 
                : "fa-exclamation-triangle"
            } mr-2`}></i>
            <div className="flex-grow-1">{message}</div>
            <button
              type="button"
              className="close"
              onClick={() => setMessage("")}
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </div>
      )}

      {/* JSON Editor Modal */}
  <JsonEditorModal
  show={jsonModalOpen}
  onClose={() => setJsonModalOpen(false)}
  item={currentItem || {}}
  onSave={handleSaveJson}
  token={token}  // Pass the token
  userid={userid}  // Pass the userid
/>


      {/* JSON View Modal */}
      <JsonViewMode
        show={jsonViewModalOpen}
        onClose={() => setJsonViewModalOpen(false)}
        item={currentItem || {}}
      />

      {/* Root File Upload Modal */}
      <RootFileUploadModal
        show={showRootFileModal}
        onClose={() => setShowRootFileModal(false)}
        token={token}
        userid={userid}
        onSuccess={() => {
          setMessage("Root file uploaded successfully!");
          // Optionally refresh data here
        }}
      />

      {/* Excel Upload Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '10px', border: 'none' }}>
              <div className="modal-header" style={{ backgroundColor: '#28a745', color: 'white', borderRadius: '10px 10px 0 0' }}>
                <h5 className="modal-title">
                  <i className="fas fa-file-excel mr-2"></i>
                  Upload Excel File
                </h5>
                <button
                  type="button"
                  className="close text-white"
                  onClick={() => !uploading && setShowModal(false)}
                  disabled={uploading}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: "500px", overflowY: "auto", padding: '30px' }}>
                {/* File input (hidden) */}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept=".xlsx,.xls,.csv"
                />

                {/* Drag and drop area */}
                <div
                  className="text-center p-4 mb-3"
                  style={{
                    border: '2px dashed #ccc',
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={triggerFileInput}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#28a745';
                    e.target.style.backgroundColor = '#e8f5e8';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#ccc';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }}
                >
                  <i className="fas fa-file-excel fa-3x mb-3 text-success"></i>
                  <h6>Drop your Excel file here</h6>
                  <p className="text-muted mb-0">or click to browse</p>
                  <small className="text-muted">Supported formats: .xlsx, .xls, .csv</small>
                </div>

                {/* Selected file info */}
                {selectedFile && (
                  <div className="mb-3 p-3 rounded" style={{ backgroundColor: '#e8f5e8', border: '1px solid #c3e6c3' }}>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-file-excel text-success mr-2"></i>
                      <div className="flex-grow-1">
                        <strong>Selected file:</strong> {selectedFile.name}
                        <br />
                        <small className="text-muted">
                          Size: {(selectedFile.size / 1024).toFixed(2)} KB
                        </small>
                      </div>
                    </div>
                  </div>
                )}

                {/* Upload progress */}
                {uploading && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="progress">
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                        role="progressbar"
                        style={{ width: `${uploadProgress}%` }}
                        aria-valuenow={uploadProgress}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                  </div>
                )}

                {/* Status messages */}
                {uploadStatus && (
                  <div className={`alert alert-${uploadStatus.type === 'success' ? 'success' : 'danger'} mb-3`}>
                    <i className={`fas ${uploadStatus.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'} mr-2`}></i>
                    {uploadStatus.message}
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ borderTop: '1px solid #e9ecef', padding: '20px 30px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={uploading}
                  style={{ borderRadius: '6px', padding: '8px 20px' }}
                >
                  <i className="fas fa-times mr-1"></i>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleUploadFile}
                  disabled={!selectedFile || uploading}
                  style={{ borderRadius: '6px', padding: '8px 20px' }}
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload mr-1"></i>
                      Upload
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AWSIOTCoreList;